/* eslint-disable max-lines */
import React from 'react'
import styled from 'styled-components'
import { color } from 'styles/colors'

const ChatbotTermsAndConditions = () => {
  return (
    <S.Container>
      <S.Text>
        These AI Terms of Use (this “Agreement”) are a binding contract between
        you or the entity you represent (“Customer,” “you,” or “your”) and Trane
        US Inc. (“Provider,” “we,” or “us”). This Agreement governs your access
        to and use of the Services. Services provided under this Agreement are
        for business or commercial, and not personal or consumer, use.
      </S.Text>

      <S.Text>
        THIS AGREEMENT TAKES EFFECT WHEN YOU CLICK THE “I ACCEPT” BUTTON BELOW
        (the “Effective Date”). BY CLICKING ON THE “I ACCEPT” BUTTON BELOW YOU
        (A) ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTAND THIS AGREEMENT; (B)
        REPRESENT AND WARRANT THAT YOU HAVE THE RIGHT, POWER, AND AUTHORITY TO
        ENTER INTO THIS AGREEMENT AND IF ENTERING INTO THIS AGREEMENT FOR AN
        ENTITY, THAT YOU HAVE THE LEGAL AUTHORITY TO BIND THAT ENTITY; AND (C)
        ACCEPT THIS AGREEMENT ON YOUR BEHALF OR ON BEHALF OF THE ENTITY YOU
        REPRESENT IF YOU ARE ENTERING INTO THIS AGREEMENT FOR AN ENTITY AND
        AGREE THAT YOU OR SUCH ENTITY, AS APPLICABLE, ARE LEGALLY BOUND BY ITS
        TERMS. IF YOU DO NOT ACCEPT THESE TERMS, YOU MAY NOT ACCESS OR USE THE
        SERVICES.
      </S.Text>

      <S.Section>
        <h3>1. Definitions.</h3>

        <S.Text>
          “AI Customer Input” means information, data, materials, text, prompts,
          images, works, code, or other content that is input, entered, posted,
          uploaded, submitted, transferred, or otherwise transmitted by or on
          behalf of Customer or any other Authorized User through an AI Feature.
        </S.Text>

        <S.Text>
          “AI Customer Output” means information, data, materials, text, images,
          code, works, or other content generated by or otherwise output from an
          AI Feature in response to an AI Customer Input.{' '}
        </S.Text>

        <S.Text>
          “AI Feature” means any feature, functionality, or component of the
          Services that incorporates, uses, depends on, or employs any AI
          Technology.{' '}
        </S.Text>

        <S.Text>
          “AI Technology” means any and all machine learning, deep learning, and
          other artificial intelligence technologies, including statistical
          learning algorithms, models (including large language models), neural
          networks, and other artificial intelligence tools or methodologies,
          all software implementations of any of the foregoing, and related
          hardware or equipment capable of generating various types of content
          (including text, images, video, audio, or computer code) based on
          user-supplied prompts.
        </S.Text>

        <S.Text>
          “Authorized User” means Customer and Customer’s employees,
          consultants, contractors, and agents (i) who are authorized by
          Customer to access and use the Services under the rights granted to
          Customer under this Agreement and (ii) for whom access to the Services
          has been purchased hereunder.
        </S.Text>

        <S.Text>
          “Customer Data” means AI Customer Input and AI Customer
          Output/information, data, and other content, in any form or medium,
          that is submitted, entered, posted, or otherwise transmitted by or on
          behalf of Customer or any other Authorized User through the Services,
          and any outputs based thereon or derived therefrom, including AI
          Customer Input and AI Customer Output. Customer Data does not include
          Aggregated Statistics.
        </S.Text>

        <S.Text>
          “Documentation” means any user manuals, handbooks, model cards, and
          guides relating to the Services provided by Provider to Customer.
        </S.Text>

        <S.Text>
          “Personal Information” means information that: (a) identifies or can
          be used to identify an individual (including, without limitation,
          names, signatures, addresses, telephone numbers, email addresses, and
          other unique identifiers); or (b) can be used to authenticate an
          individual (including, without limitation, employee identification
          numbers, government-issued identification numbers, passwords or PINs,
          user identification and account access credentials or passwords,
          financial account numbers, credit report information, student
          information, biometric, health, genetic, medical, or medical insurance
          data, answers to security questions, an individual’s internet activity
          or similar interaction history, inferences drawn from other personal
          information to create consumer profiles, geolocation data, an
          individual’s commercial, employment, or education history, and other
          personal characteristics and identifiers). Customer’s business contact
          information is not by itself deemed to be Personal Information.
        </S.Text>

        <S.Text>
          “Process” means to take any action or perform any operation or set of
          operations that the Services are capable of taking or performing on
          any data, information, or other content, including to collect,
          receive, input, upload, download, record, reproduce, store, organize,
          combine, log, catalog, cross-reference, manage, maintain, copy, adapt,
          alter, translate, or make other improvements or derivative works,
          process, weigh, perform statistical analysis, retrieve, output,
          consult, use, perform, display, disseminate, transmit, submit, post,
          transfer, disclose, or otherwise provide or make available, or block,
          erase, or destroy. “Processing” and “Processed” have correlative
          meanings.{' '}
        </S.Text>

        <S.Text>
          “Provider IP” means the Services, the Documentation, and all
          intellectual property provided to Customer or any other Authorized
          User in connection with the foregoing. For the avoidance of doubt,
          Provider IP includes Aggregated Statistics and any information, data,
          or other content derived from Provider’s monitoring of Customer’s
          access to or use of the Services, but does not include Customer Data.
          Provider IP includes all modifications, enhancements, refinements,
          adaptations, customizations, improvements, and derivative works of the
          Services.
        </S.Text>

        <S.Text>
          “Services” means the services provided by Provider under this
          Agreement including the AI Features and any APIs.
        </S.Text>

        <S.Text>
          “Third-Party Products” means any products, technology, content, data,
          services, information, websites, or other materials that are owned by
          third parties and are included in, incorporated into, or accessible
          through the Services, including any third-party AI Technology.
        </S.Text>

        <S.Text>
          “Training Data” means any and all information, data, materials, text,
          prompts, images, code, and other content that is used by or on behalf
          of Provider to train, validate, test, retrain, or improve any AI
          Technology incorporated into or used with, in connection with, or in
          support of, the Services.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>2. Access and Use. </h3>
        <S.Text>
          (a) Provision of Access. Subject to and conditioned on your compliance
          with all the terms and conditions of this Agreement, Provider hereby
          grants you a non-exclusive, non-transferable right to access and use
          the Services during the Term, solely for your personal or internal
          business operations by Authorized Users under these terms and
          conditions. Provider shall provide you the necessary passwords and
          access credentials to allow you access to the Services.
        </S.Text>
        <S.Text>
          (b) Documentation License. Subject to the terms and conditions
          contained in this Agreement, Provider hereby grants you a
          non-exclusive, non-sublicensable, non-transferable license for
          Authorized Users to use the Documentation during the Term solely for
          your internal business purposes in connection with use of the
          Services.
        </S.Text>
        <S.Text>
          (c) Use Restrictions. You shall not use the Services for any purposes
          beyond the scope of the access granted in this Agreement. You shall
          not at any time, directly or indirectly, and shall not permit any
          Authorized Users to: (i) copy, modify, or create derivative works of
          the Services or Documentation, in whole or in part; (ii) rent, lease,
          lend, sell, license, sublicense, assign, distribute, publish,
          transfer, or otherwise make available the Services, or Documentation;
          (iii) reverse engineer, disassemble, decompile, decode, or duplicate
          the Services, reproduce Training Data, engage in model extraction, or
          otherwise attempt to derive or gain access to any source code,
          algorithm, model, model weights and parameters, or other underlying AI
          Technology or component of the Services, in whole or in part; (iv)
          access or use the Services to develop, train, or improve any other AI
          Technology; (v) use web scraping, web harvesting, web data extraction
          or any other method to extract data from the Services; (vi) remove any
          proprietary notices from the Services or Documentation; (vii) use the
          Services to create or generate AI Customer Output, or use AI Customer
          Output in a manner, that you know or should know infringes,
          misappropriates, or otherwise violates any intellectual property right
          or other right of any person, or that violates any applicable law,
          regulation, or rule; or (viii) submit, enter, post, or otherwise
          transmit or Process any Personal Information through the
          Services.
        </S.Text>
        <S.Text>
          (d) Aggregated Statistics. Notwithstanding anything to the contrary in
          this Agreement, Provider may monitor Customer’s use of the Services
          and collect and compile data and information related to Customer’s use
          of the Services to be used by Provider in an aggregated and anonymized
          manner, including to compile statistical and performance information
          related to the provision and operation of the Services (“Aggregated
          Statistics”). As between Provider and Customer, all right, title, and
          interest in Aggregated Statistics, and all intellectual property
          rights therein, belong to and are retained solely by Provider. You
          agree that Provider may use and make publicly available Aggregated
          Statistics to the extent and in the manner permitted under applicable
          law.
        </S.Text>
        <S.Text>
          (e) Reservation of Rights. Provider reserves all rights not expressly
          granted to Customer in this Agreement. Except for the limited rights
          and licenses expressly granted under this Agreement, nothing in this
          Agreement grants, by implication, waiver, estoppel, or otherwise, to
          Customer or any third party, any intellectual property rights or other
          right, title, or interest in or to the Provider IP or Third-Party
          Products.
        </S.Text>
        <S.Text>
          (f) Suspension. Notwithstanding anything to the contrary in this
          Agreement, Provider may temporarily suspend Customer's and any other
          Authorized User's access to any portion or all of the Services if:
          (i) Provider reasonably determines that (A) there is a threat or
          attack on any of the Provider IP; (B) Customer's or any other
          Authorized User's use of the Provider IP disrupts or poses a security
          risk to the Provider IP, to Provider, or to any other customer or
          vendor of Provider; (C) Customer or any other Authorized User is
          using the Provider IP for fraudulent or illegal activities;
          (D) subject to applicable law, Customer has ceased to continue its
          business in the ordinary course, made an assignment for the benefit
          of creditors or similar disposition of its assets, or become the
          subject of any bankruptcy, reorganization, liquidation, dissolution,
          or similar proceeding; (E) Provider's provision of the Services to
          Customer or any other Authorized User is prohibited by applicable
          law; or (F) Customer is using the Services in material violation of
          Section 2(c); or (ii) any vendor of Provider has suspended or
          terminated Provider's access to or use of any third-party services
          or products required to enable Customer to access and use the
          Services; (any such suspension a "Service Suspension"). Provider
          shall use commercially reasonable efforts to provide written notice
          of any Service Suspension to Customer and to provide updates regarding
          resumption of access to the Services following any Service Suspension.
          Provider shall use commercially reasonable efforts to resume providing
          access to the Services as soon as reasonably possible after the event
          giving rise to the Services Suspension is cured. Provider will have
          no liability for any damage, liabilities, losses (including any loss
          of data or profits), or any other consequences that Customer or any
          other Authorized User may incur as a result of a Service Suspension.
        </S.Text>

      </S.Section>

      <S.Section>
        <h3>3. Customer Responsibilities. </h3>
        <S.Text>
          (a) Acceptable Use Policy; Provider Policies. The Services may not be
          used for unlawful, fraudulent, offensive, or obscene activity. You
          shall comply with all terms and conditions of this Agreement, all
          applicable laws, rules, and regulations, and all guidelines,
          standards, requirements, and policies that may be posted on
          https://www.tranetechnologies.com/en/index/terms-of-use.html from time
          to time, which are hereby incorporated herein by reference.
        </S.Text>
        <S.Text>
          (b) Account Use. You are responsible and liable for all uses of the
          Services and Documentation resulting from access provided by you,
          directly or indirectly, whether that access or use is permitted by or
          in violation of this Agreement. Without limiting the generality of the
          foregoing, you are responsible for all acts and omissions of
          Authorized Users, and any act or omission by an Authorized User that
          would constitute a breach of this Agreement if taken by you will be
          deemed a breach of this Agreement by you. You shall use reasonable
          efforts to make all Authorized Users aware of this Agreement’s
          provisions as applicable to such Authorized User’s use of the Services
          and shall cause Authorized Users to comply with such provisions.
        </S.Text>
        <S.Text>
          (c) Use of AI Customer Output. You are solely responsible for (i)
          evaluating (including by human review) AI Customer Output for
          accuracy, completeness, and other factors relevant to your use before
          using, distributing, or relying on the AI Customer Output and (ii)
          your decisions, actions, and omissions in reliance or based on the AI
          Customer Output.
        </S.Text>
        <S.Text>
          (d) Passwords and Access Credentials. You are responsible for keeping
          your passwords and access credentials associated with the Services
          confidential. You shall not sell or transfer them to any other person
          or entity. You shall promptly notify us about any unauthorized access
          to your passwords or access credentials.
        </S.Text>
        <S.Text>
          (e) Third-Party Products. The Services may permit access to
          Third-Party Products. For purposes of this Agreement, these
          Third-Party Products are subject to their own terms and conditions
          which may be presented to you for acceptance by website link or
          otherwise. The Services may also include or incorporate Third-Party
          Products licensed or provided by third parties that require us to pass
          through additional terms to you. You shall comply with all such
          applicable pass-through terms as such terms may be updated, modified,
          or added from time to time. We may add or remove Third-Party Products
          from time to time. If you do not agree to abide by the applicable
          terms for any Third-Party Products, then you should not install,
          access, or use these Third-Party Products or any Services that include
          or incorporate these Third-Party Products.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>4. Confidential Information.</h3>
        <S.Text>
          From time to time during the Term, Provider and Customer may disclose
          or make available to the other party information about its business
          affairs, products, confidential intellectual property, trade secrets,
          third-party confidential information, and other sensitive or
          proprietary information, whether orally or in written, electronic, or
          other form or media/in written or electronic form or media, whether or
          not marked, designated, or otherwise identified as “confidential” at
          the time of disclosure (collectively, “Confidential Information”).
          Without limiting the foregoing, Provider IP is Provider’s Confidential
          Information. Confidential Information does not include information
          that, at the time of disclosure is: (a) in the public domain; (b)
          known to the receiving party; (c) rightfully obtained by the receiving
          party on a non-confidential basis from a third party; or (d)
          independently developed by the receiving party. The receiving party
          shall not disclose the disclosing party’s Confidential Information to
          any person or entity, except to the receiving party’s employees,
          agents, or subcontractors who have a need to know the Confidential
          Information for the receiving party to exercise its rights or perform
          its obligations hereunder and who are required to protect the
          Confidential Information in a manner no less stringent than required
          under this Agreement. Notwithstanding the foregoing, each party may
          disclose Confidential Information to the limited extent required (i)
          to comply with the order of a court or other governmental body, or as
          otherwise necessary to comply with applicable law, provided that the
          party making the disclosure pursuant to the order shall first have
          given written notice to the other party and made a reasonable effort
          to obtain a protective order; or (ii) to establish a party’s rights
          under this Agreement, including to make required court filings. Each
          party’s obligations of non-disclosure regarding Confidential
          Information are effective as of the date the Confidential Information
          is first disclosed to the receiving party and will continue as long as
          permitted by applicable law; provided, however, for any Confidential
          Information that constitutes a trade secret (as determined under
          applicable law), those obligations of non-disclosure will survive the
          termination or expiration of this Agreement for as long as the
          Confidential Information remains subject to trade secret protection
          under applicable law.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>5. Privacy Policy.</h3>
        <S.Text>
          Provider complies with its privacy policy, available at
          https://www.tranetechnologies.com/en/index/privacy-policy.html
          (“Privacy Policy”), in providing the Services. The Privacy Policy is
          subject to change as described therein. By accessing, using, and
          providing information to or through the Services, you acknowledge that
          you have reviewed and accepted our Privacy Policy, and you consent to
          all actions taken by us with respect to your information in compliance
          with the then-current version of our Privacy Policy.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>6. Intellectual Property Ownership.</h3>
        <S.Text>
          (a) Provider IP. Customer acknowledges that, as between Customer and
          Provider, Provider owns all right, title, and interest, including all
          intellectual property rights, in and to the Provider IP and, for
          Third-Party Products, the applicable third-party providers own all
          right, title, and interest, including all intellectual property
          rights, in and to the Third-Party Products.
        </S.Text>
        <S.Text>
          (b) Customer Data. Provider acknowledges that, as between Provider and
          Customer, Customer owns all right, title, and interest, including all
          intellectual property rights, in and to the Customer Data,
          incorporated into AI Customer Output and subject to the license
          granted herein. Customer hereby grants to Provider a non-exclusive,
          royalty-free, worldwide license to (i) reproduce, distribute, and
          otherwise use and display the Customer Data and Process the Customer
          Data as may be necessary for Provider to provide the Services to
          Customer and (ii) use, modify, and adapt AI Customer Input and AI
          Customer Output to train, develop, adapt, modify, enhance, or improve
          the AI Features and other products or services. Notwithstanding
          anything in this Agreement to the contrary, unless prohibited by
          applicable law, we may delete Customer Data at any time if we
          determine that Customer Data violates the terms of this Agreement or
          that deletion is necessary to comply with applicable law.
        </S.Text>
        <S.Text>
          (c) Feedback. If you or any other Authorized User sends or transmits
          any communications or materials to us by mail, email, telephone, or
          otherwise, suggesting or recommending changes to the Services,
          including without limitation, new features or functionality relating
          thereto, or any comments, questions, suggestions, or the like
          (“Feedback”), we are free to use that Feedback. All Feedback is and
          will be treated as non-confidential. You hereby assign to us on your
          behalf, and shall cause your Authorized Users to assign to us, all
          right, title, and interest in, and we are free to use, without any
          attribution or compensation to you or any third party, any ideas,
          know-how, concepts, techniques, or other intellectual property rights
          contained in the Feedback, for any purpose whatsoever, although we are
          have no obligation to acknowledge receipt of or use any Feedback.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>7. Limited Warranty and Warranty Disclaimer. </h3>
        <S.Text>
          (a) Limited Provider Warranty. Provider warrants that the Services
          will conform in all material respects to the Documentation when
          accessed and used by Customer in accordance with this Agreement. Your
          sole remedy and Provider’s sole liability for breach of the foregoing
          warranty is for Provider to use reasonable efforts to correct the
          Services to conform to the Documentation. Provider does not make any
          representations or guarantees regarding uptime or availability of the
          Services. THE FOREGOING WARRANTY DOES NOT APPLY, AND PROVIDER STRICTLY
          DISCLAIMS ALL WARRANTIES, WITH RESPECT TO ANY THIRD-PARTY
          PRODUCTS.
        </S.Text>
        <S.Text>
          (b) Customer Warranty. You represent, warrant, and covenant that (i)
          you own or otherwise have and will have all necessary rights,
          permissions, and consents in and relating to the Customer Data so
          that, as received by Provider and Processed in accordance with this
          Agreement, it does not and will not infringe, misappropriate, or
          otherwise violate any intellectual property rights, or any privacy or
          other rights of any third party or violate any applicable law, and
          (ii) no Customer Data contains or will contain any Personal
          Information.
        </S.Text>
        <S.Text>
          (c) EXCEPT FOR THE LIMITED WARRANTY SET OUT IN Section 7(a), THE
          SERVICES AND AI CUSTOMER OUTPUT ARE PROVIDED “AS IS” AND PROVIDER
          SPECIFICALLY DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED,
          STATUTORY, OR OTHERWISE. PROVIDER SPECIFICALLY DISCLAIMS ALL IMPLIED
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          TITLE, AND NON-INFRINGEMENT, AND ALL WARRANTIES ARISING FROM COURSE OF
          DEALING, USAGE, OR TRADE PRACTICE. PROVIDER MAKES NO WARRANTY OF ANY
          KIND THAT THE SERVICES, OR ANY PRODUCTS OR RESULTS OF THE USE THEREOF,
          INCLUDING ANY AI OUTPUTS, WILL MEET YOUR OR ANY OTHER PERSON’S OR
          ENTITY’S REQUIREMENTS, OPERATE WITHOUT INTERRUPTION, ACHIEVE ANY
          INTENDED RESULT, BE COMPATIBLE OR WORK WITH ANY OF YOUR OR ANY THIRD
          PARTY’S SOFTWARE, SYSTEM, OR OTHER SERVICES, OR BE SECURE, ACCURATE,
          COMPLETE, FREE OF HARMFUL CODE, OR ERROR-FREE, OR THAT ANY ERRORS OR
          DEFECTS CAN OR WILL BE CORRECTED. YOU ACKNOWLEDGE THAT, GIVEN THE
          NATURE OF THE SERVICES AND AI TECHNOLOGY, AI CUSTOMER OUTPUT (I) MAY
          BE INACCURATE, MISLEADING, BIASED, OR OFFENSIVE, (II) MAY BE THE SAME
          AS OR SIMILAR TO OUTPUT THE SERVICES GENERATE FOR OTHER CUSTOMERS,
          (III) MAY NOT QUALIFY FOR INTELLECTUAL PROPERTY PROTECTION, AND (IV)
          MAY BE SUBJECT TO THIRD PARTY TERMS, INCLUDING, AS APPLICABLE, OPEN
          SOURCE LICENSES, AND (V) DO NOT NECESSARILY REFLECT, AND MAY BE
          INCONSISTENT WITH, PROVIDER’S AND THIRD-PARTY PROVIDERS’ VIEWS.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>8. Indemnification.</h3>
        <S.Text>
          Customer shall indemnify, hold harmless, and, at Provider’s option,
          defend Provider and its officers, directors, employees, agents,
          affiliates, successors, and assigns from and against any and all
          Losses arising from or relating to any Third-Party Claim (i) that the
          AI Customer Input or other Customer Data other than AI Customer
          Output, or Processing or any other use thereof in accordance with this
          Agreement, infringes or misappropriates such third party’s
          intellectual property rights; or (ii) based on Customer’s or any
          Authorized User’s negligence or willful misconduct or use of the
          Services in a manner not authorized by this Agreement or applicable
          laws; provided that Customer may not settle any Third-Party Claim
          against Provider unless Provider consents to such settlement, and
          further provided that Provider will have the right, at its option, to
          defend itself against any such Third-Party Claim or to participate in
          the defense thereof by counsel of its own choice.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>9. Limitations of Liability.</h3>
        <S.Text>
          EXCEPT AS PROHIBITED BY LAW, IN NO EVENT WILL PROVIDER BE LIABLE UNDER
          OR IN CONNECTION WITH THIS AGREEMENT UNDER ANY LEGAL OR EQUITABLE
          THEORY, INCLUDING BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE),
          STRICT LIABILITY, OR OTHERWISE, FOR ANY: (a) CONSEQUENTIAL,
          INCIDENTAL, INDIRECT, EXEMPLARY, SPECIAL, ENHANCED, OR PUNITIVE
          DAMAGES; (b) INCREASED COSTS, DIMINUTION IN VALUE OR LOST BUSINESS,
          PRODUCTION, REVENUES, OR PROFITS; (c) LOSS OF GOODWILL OR REPUTATION;
          (d) USE, INABILITY TO USE, LOSS, INTERRUPTION, DELAY OR RECOVERY OF
          ANY DATA, OR BREACH OF DATA OR SYSTEM SECURITY; OR (e) COST OF
          REPLACEMENT GOODS OR SERVICES, IN EACH CASE REGARDLESS OF WHETHER
          PROVIDER WAS ADVISED OF THE POSSIBILITY OF SUCH LOSSES OR DAMAGES OR
          SUCH LOSSES OR DAMAGES WERE OTHERWISE FORESEEABLE. EXCEPT AS
          PROHIBITED BY LAW, IN NO EVENT WILL PROVIDER’S AGGREGATE LIABILITY
          ARISING OUT OF OR RELATED TO THIS AGREEMENT UNDER ANY LEGAL OR
          EQUITABLE THEORY, INCLUDING BREACH OF CONTRACT, TORT (INCLUDING
          NEGLIGENCE), STRICT LIABILITY, OR OTHERWISE EXCEED $100.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>10. Termination.</h3>
        <S.Text>
          (a) Termination. In addition to any other express termination right
          set out in this Agreement: (i) Provider may terminate this Agreement
          for any reason upon thirty (30) days’ advance notice. (ii) Either
          party may terminate this Agreement, effective on written notice to the
          other party, if the other party breaches this Agreement, and such
          breach: (A) is incapable of cure; or (B) being capable of cure,
          remains uncured 30 days after the non-breaching party provides the
          breaching party with written notice of such breach. (iii) Either party
          may terminate this Agreement, effective immediately upon written
          notice to the other party, if the other party: (A) becomes insolvent
          or is generally unable to pay, or fails to pay, its debts as they
          become due; (B) files, or has filed against it, a petition for
          voluntary or involuntary bankruptcy or otherwise becomes subject,
          voluntarily or involuntarily, to any proceeding under any domestic or
          foreign bankruptcy or insolvency law; (C) makes or seeks to make a
          general assignment for the benefit of its creditors; or (D) applies
          for or has appointed a receiver, trustee, custodian, or similar agent
          appointed by order of any court of competent jurisdiction to take
          charge of or sell any material portion of its property or
          business.
        </S.Text>
        <S.Text>
          (b) Effect of Expiration or Termination. Upon expiration or
          termination of this Agreement, Customer shall immediately discontinue
          use of the Provider IP. Following expiration or termination of this
          Agreement, Provider may permanently delete Customer Data from the
          Services and all systems Provider controls, unless otherwise required
          by applicable law. Notwithstanding the foregoing and for the avoidance
          of doubt, Provider shall not be obligated to delete, destroy, or
          disable any modifications, developments, or improvements to the AI
          Features or any other products or services resulting from Provider’s
          use of Customer Data.
        </S.Text>
        <S.Text>
          (c) Survival. This Section and any right, obligation, or required
          performance of the parties in this Agreement which, by its express
          terms or nature and context is intended to survive termination or
          expiration of this Agreement, will survive termination or
          expiration.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>11. Modifications.</h3>
        <S.Text>
          You acknowledge and agree that we have the right, in our sole
          discretion, to modify this Agreement from time to time, and that
          modified terms become effective on posting. You will be notified of
          modifications through notifications. You are responsible for reviewing
          and becoming familiar with any modifications. Your continued use of
          the Services after the effective date of the modifications will be
          deemed acceptance of the modified terms.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>12. Export Regulation.</h3>
        <S.Text>
          The Services utilize software and technology that may be subject to US
          export control laws, including the US Export Administration Act and
          its associated regulations. You shall not, directly or indirectly,
          export, re-export, or release the Services or the software or
          technology included in the Services to, or make the Services or the
          software or technology included in the Services accessible from, any
          jurisdiction or country to which export, re-export, or release is
          prohibited by law, regulation, or rule. You shall comply with all
          applicable federal laws, regulations, and rules, and complete all
          required undertakings (including obtaining any necessary export
          license or other governmental approval), prior to exporting,
          re-exporting, releasing, or otherwise making the Services or the
          software or technology included in the Services available outside the
          US.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>13. Governing Law and Jurisdiction.</h3>
        <S.Text>
          This agreement is governed by and construed in accordance with the
          internal laws of the State of North Carolina without giving effect to
          any choice or conflict of law provision or rule that would require or
          permit the application of the laws of any jurisdiction other than
          those of the State of North Carolina. Any legal suit, action, or
          proceeding arising out of this agreement or the rights granted
          hereunder will be instituted in the federal courts of the United
          States or the courts of the State of North Carolina in each case
          located in the city of Charlotte and County of Mecklenburg, and each
          party irrevocably submits to the jurisdiction of such courts in any
          such suit, action, or proceeding.
        </S.Text>
      </S.Section>

      <S.Section>
        <h3>14. Miscellaneous.</h3>
        <S.Text>
          This Agreement constitutes the entire agreement and understanding
          between the parties hereto with respect to the subject matter hereof
          and supersedes all prior and contemporaneous understandings,
          agreements, representations, and warranties, both written and oral,
          with respect to such subject matter. Any notices to us must be sent to
          or our corporate headquarters address available at
          https://www.tranetechnologies.com and must be delivered either in
          person, by email, certified or registered mail, return receipt
          requested and postage prepaid, or by recognized overnight courier
          service, and are deemed given upon receipt by us. Notwithstanding the
          foregoing, you hereby consent to receiving electronic communications
          from us. These electronic communications may include notices about
          applicable fees and charges, transactional information, and other
          information concerning or related to the Services. You agree that any
          notices, agreements, disclosures, or other communications that we send
          to you electronically will satisfy any legal communication
          requirements, including that such communications be in writing. The
          invalidity, illegality, or unenforceability of any provision herein
          does not affect any other provision herein or the validity, legality,
          or enforceability of such provision in any other jurisdiction. Any
          failure to act by us with respect to a breach of this Agreement by you
          or others does not constitute a waiver and will not limit our rights
          with respect to such breach or any subsequent breaches. This Agreement
          is personal to you and may not be assigned or transferred for any
          reason whatsoever without our prior written consent and any action or
          conduct in violation of the foregoing will be void and without effect.
          We expressly reserve the right to assign this Agreement and to
          delegate any of its obligations hereunder.
        </S.Text>
      </S.Section>
    </S.Container>
  )
}

export default ChatbotTermsAndConditions

const S = {
  Container: styled.div`
    font-family: "Inter", regular;
    font-size: 16px;
    color: ${color('chatbotReply')};
  `,
  Section: styled.section`
    display: block;
    margin: 20px 0px;
  `,
  Text: styled.p`
    display: block;
    margin: 0 0 16px;
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }
  `
}
